import * as React from "react";

import { GenericDropdownBuilder } from "../../form/dropdown/index";
import RadioSelect from "@components/form/radio-select";
import styled from "styled-components";
import { SimpleRelease } from '@common/types/release/simple-release';
import SimpleTrack from '@common/types/release/simple-track';
import AppState from '@webapp/store/state';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { ListReleases } from '@webapp/store/release/action-creators';
import { ReleaseStatus } from '@common/types/release/relase-status';

interface TrackSelectorCtrlProps {
  className?: string;
  releases: SimpleRelease[];
  tracks: SimpleTrack[];
  activeReleaseId?: string;
  artistId?: string;
  getReleases: () => ListReleases;
  onSelection: (
    track: SimpleTrack,
    status: ReleaseStatus
  ) => void;
}

const DropdownCtrl = GenericDropdownBuilder();

const mapStateToProps = (state: AppState) => ({
  activeReleaseId: state.release.release?.id,
  artistId: state.artist.active?.id,
  releases: state.release.list.published || [],
  tracks: Object.values(state.tracks.tracks) || [],
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getReleases: () => dispatch(new ListReleases('published', true)),
})

export const TrackSelectorCtrl: React.FC<TrackSelectorCtrlProps> = (
  props
) => {

  const {artistId, tracks, releases, getReleases, onSelection} = props;

  const [filter, setFilter] = React.useState("");

  const [activeRelease, setActiveRelease] = React.useState("");
  const [activeTrack, setActiveTrack] = React.useState("");

  React.useEffect(() => {
    if (artistId) {
      getReleases();
    }
  }, [getReleases, artistId]);

  React.useEffect(() => {
    if (activeTrack) {
      const track = tracks.find((t) => t.id === activeTrack);
      const release = releases.find((r) => r.id === track?.releaseId);

      if (track && release) {
        onSelection(track, release.status);
      }
    }
  }, [activeTrack, tracks, releases, onSelection]);

  const getTracks = (releaseId: string) => {
    return tracks
      .filter((t) => t.releaseId === releaseId)
      .filter((t) => t.title.toLowerCase().includes(filter.toLowerCase().trim()))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
  };

  const releaseList = releases
    .filter((release) => {
      if (!filter) {
        return true;
      }

      const hasTracks = getTracks(release.id).length > 0;

      return hasTracks || release.title.toLowerCase().includes(filter.toLowerCase().trim())
    })
    .sort((a, b) => a.title.localeCompare(b.title))
  ;

  const getActiveTitle = () => {
    if (activeTrack) {
      const track = tracks.find((t) => t.id === activeTrack);

      return track?.title;
    }
  }

  const handleReleaseSelection = (release: SimpleRelease) => {
    setActiveRelease(release.id);

    if (release.type === 'single') {
      const trackList = getTracks(release.id);
      if (trackList.length) {
        setActiveTrack(trackList[0].id)
      }
    } else {
      setActiveTrack("");
    }
  }

  const handleTrackSelection = (releaseId: string, track: SimpleTrack) => {
    setActiveRelease(releaseId);
    setActiveTrack(track.id);
  }

  return (
    <div className={props.className}>
      <DropdownCtrl
        title="Select a published Track"
        value={getActiveTitle()}
        hasFilter
        filterLabel="Filter Releases"
        customFilterValue={filter}
        onCustomFilter={setFilter}
      >
        <div>
          {
            releaseList.map((release) => (
              <div key={`release-${release.id}`}>
                <RadioSelect
                  className={`radio-release ${release.type === 'album' ? 'radio-album' : ''}`}
                  isChecked={activeRelease === release.id}
                  onChange={() => handleReleaseSelection(release)}
                  hasArtwork
                  artwork={release.cover.url}
                  isAlbum={release.type === 'album'}
                >
                  {release.title}
                </RadioSelect>

                {
                  release.type === 'album' ? (
                    getTracks(release.id).map((track: SimpleTrack) => (
                      <RadioSelect
                        key={`track-${track.id}`}
                        className="radio-release"
                        isChecked={activeTrack === track.id}
                        onChange={() => handleTrackSelection(release.id, track)}
                        albumTrack
                      >
                        { track.title }
                      </RadioSelect>
                    ))
                  ) : null
                }
              </div>
            ))
          }
        </div>
      </DropdownCtrl>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(styled(TrackSelectorCtrl)`
  .radio-release {
    margin-bottom: 15px;
    opacity: 1;

    &.radio-album {
      opacity: .5;
    }

    &.album-track {
      margin-bottom: 13px;
    }
  }
`);
